import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import BackBtn from "../../widgets/BackBtn";
import {
  FileArchive,
  Download,
  Search,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Clock,
  CheckCircle2,
  Megaphone,
  Hash,
} from "lucide-react";
import useApiEp from "../../hooks/useApiEp";

const STATUS_MAP = {
  1: { label: "Pending", color: "warning" },
  2: { label: "Ready", color: "success" },
};

const TYPE_COLOR = {
  full: "primary",
  bulk: "secondary",
};

const DownloadReports = () => {
  const { data, isLoading, isError, error } = useApiEp(
    `/voice/download-reports`
  );

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("reqDate");
  const [sortDir, setSortDir] = useState("desc");
  const [typeFilter, setTypeFilter] = useState("all");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field)
      return <ArrowUpDown size={13} style={{ opacity: 0.35 }} />;
    return sortDir === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />;
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = [...data];

    if (typeFilter !== "all") {
      rows = rows.filter((r) => r.reportType === typeFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.reportType?.toLowerCase().includes(q) ||
          r.reqDate?.toLowerCase().includes(q) ||
          STATUS_MAP[r.status]?.label.toLowerCase().includes(q) ||
          r.campaignName?.toLowerCase().includes(q) ||
          String(r.campaignId ?? "").includes(q)
      );
    }

    rows.sort((a, b) => {
      let aVal = a[sortField] ?? "";
      let bVal = b[sortField] ?? "";
      const cmp = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
      });
      return sortDir === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [data, search, sortField, sortDir, typeFilter]);

  const handleDownload = (url, reqDate) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = `report_${reqDate?.replace(/[: ]/g, "-")}.zip`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const SortableHeader = ({ label, field, align = "left" }) => (
    <TableCell
      align={align}
      onClick={() => handleSort(field)}
      sx={{
        cursor: "pointer",
        fontWeight: 700,
        color: "text.secondary",
        fontSize: "0.72rem",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        whiteSpace: "nowrap",
        userSelect: "none",
        "&:hover": { color: "primary.main" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {label}
        <SortIcon field={field} />
      </Box>
    </TableCell>
  );

  const reportTypes = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.map((r) => r.reportType).filter(Boolean))];
  }, [data]);

  return (
    <Container sx={{ mt: 3, mb: 6 }}>
      <BackBtn />

      {/* ── Header ── */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 3, mb: 3 }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FileArchive size={22} color="#fff" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            Download Reports
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filtered.length} of {data?.length ?? 0} report
            {data?.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </Box>

      {/* ── Filters Bar ── */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2.5,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="Search campaign, date, status…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={15} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="all">All types</MenuItem>
            {reportTypes.map((t) => (
              <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortField}
            label="Sort by"
            onChange={(e) => setSortField(e.target.value)}
          >
            <MenuItem value="reqDate">Date</MenuItem>
            <MenuItem value="reportType">Type</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="campaignName">Campaign</MenuItem>
            <MenuItem value="campaignId">Campaign ID</MenuItem>
          </Select>
        </FormControl>

        <Tooltip title={sortDir === "asc" ? "Ascending" : "Descending"}>
          <IconButton
            size="small"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
            }}
          >
            {sortDir === "asc" ? (
              <ArrowUp size={17} />
            ) : (
              <ArrowDown size={17} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Loading ── */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* ── Error ── */}
      {isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error?.message ?? "Failed to load reports."}
        </Alert>
      )}

      {/* ── Table ── */}
      {!isLoading && !isError && (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <SortableHeader label="Type" field="reportType" />
                <SortableHeader label="Campaign" field="campaignName" />
                <SortableHeader label="Campaign ID" field="campaignId" />
                <SortableHeader label="Requested On" field="reqDate" />
                <SortableHeader label="Status" field="status" />
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    fontSize: "0.72rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    No reports found{search ? ` for "${search}"` : ""}.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row, i) => {
                  const statusInfo = STATUS_MAP[row.status] ?? {
                    label: "Unknown",
                    color: "default",
                  };
                  const isReady = row.status === "2" && row.reportUrl;
                  const hasCampaign = Boolean(
                    row.campaignId || row.campaignName
                  );

                  return (
                    <TableRow
                      key={i}
                      hover
                      sx={{ "&:last-child td": { border: 0 } }}
                    >
                      {/* Type */}
                      <TableCell>
                        <Chip
                          label={row.reportType}
                          color={TYPE_COLOR[row.reportType] ?? "default"}
                          size="small"
                          icon={<FileArchive size={11} />}
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>

                      {/* Campaign Name */}
                      <TableCell>
                        {hasCampaign ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.8,
                            }}
                          >
                            <Megaphone
                              size={13}
                              style={{ opacity: 0.45, flexShrink: 0 }}
                            />
                            <Typography variant="body2" fontWeight={500}>
                              {row.campaignName ?? "—"}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* Campaign ID */}
                      <TableCell>
                        {row.campaignId ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.6,
                            }}
                          >
                            <Hash size={12} style={{ opacity: 0.4 }} />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontFamily="monospace"
                            >
                              {row.campaignId}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* Date */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.7,
                          }}
                        >
                          <Clock size={12} style={{ opacity: 0.4 }} />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            whiteSpace="nowrap"
                          >
                            {row.reqDate}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                          icon={
                            row.status === "2" ? (
                              <CheckCircle2 size={11} />
                            ) : undefined
                          }
                          sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                        />
                      </TableCell>

                      {/* Download */}
                      <TableCell>
                        {isReady ? (
                          <Tooltip
                            title={`Download ${row.reportUrl
                              ?.split("/")
                              .pop()}`}
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                handleDownload(row.reportUrl, row.reqDate)
                              }
                              sx={{
                                bgcolor: "primary.50",
                                "&:hover": { bgcolor: "primary.100" },
                              }}
                            >
                              <Download size={16} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Report not ready yet">
                            <span>
                              <IconButton size="small" disabled>
                                <Download size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default DownloadReports;
